import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { FONT_PAIRINGS, applyFontPairing, getFontPairing } from "@/lib/fonts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ImageUploadField } from "@/components/ImageUploadField";
import { THEME_PRESETS, applyBrandTheme } from "@/lib/brandTheme";
import { Save, Upload, X, Compass, ListChecks, Plus, Trash2, Send, Eye } from "lucide-react";
import { useTour } from "@/lib/tour";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { EmailPanel } from "@/components/EmailPanel";
import { ClientPreviewDialog } from "@/components/ClientPreviewDialog";
import { TeamPanel } from "@/components/TeamPanel";
import { ActivityPanel } from "@/components/ActivityPanel";
import { useNavigate } from "react-router-dom";

const MODULES = ["today","contacts","projects","finance","calendar","tasks","documents","campaigns","news","analytics","data","settings"];

export default function SettingsPage() {
  const { settings, update, refresh } = useSettings() || {};
  const { user, logoutEverywhere } = useAuth();
  const { startTour } = useTour();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [me, setMe] = useState(null);
  const [resetSummary, setResetSummary] = useState(null);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isAdmin = user?.role !== "member";

  useEffect(() => { setF(settings); }, [settings]);
  // Live-preview the accent while you pick it; the saved values are re-applied on exit.
  useEffect(() => {
    if (!f) return;
    applyBrandTheme({ accent: f.primary_color, surface: f.secondary_color, dark: theme === "dark" });
  }, [f?.primary_color, f?.secondary_color, theme]);  // eslint-disable-line react-hooks/exhaustive-deps
  const savedBrand = useRef(null);
  useEffect(() => { savedBrand.current = settings; }, [settings]);
  useEffect(() => () => {
    const saved = savedBrand.current;
    if (saved) applyBrandTheme({ accent: saved.primary_color, surface: saved.secondary_color,
                                 dark: document.documentElement.classList.contains("dark") });
  }, []);
  useEffect(() => { api.get("/auth/me").then(({ data }) => setMe(data)); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/workspace/summary").then(({ data }) => setResetSummary(data)).catch((err) => console.error("Could not load workspace summary", err));
  }, [resetting, isAdmin]);
  const loadReminderPreview = () => api.get("/invoices/reminders/preview").then(({ data }) => setReminderPreview(data)).catch((err) => console.error("Could not load reminder preview", err));
  useEffect(() => { if (isAdmin) loadReminderPreview(); /* eslint-disable-next-line */ }, [isAdmin]);

  const saveProfile = async () => {
    try { await api.put("/auth/me", { name: me.name }); toast.success("Profile updated"); }
    catch { toast.error("Failed"); }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your profile and your activity. Workspace settings are managed by an admin.
          </p>
        </div>
        <Tabs defaultValue="profile">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger data-testid="tab-profile" value="profile">Profile</TabsTrigger>
            <TabsTrigger data-testid="tab-activity" value="activity">My activity</TabsTrigger>
          </TabsList>
          <TabsContent value="profile">
            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Your profile</CardTitle></CardHeader>
              <CardContent className="space-y-3 max-w-md">
                {me && <>
                  <Field label="Name"><Input data-testid="profile-name" value={me.name} onChange={(e) => setMe({ ...me, name: e.target.value })}/></Field>
                  <Field label="Email"><Input value={me.email} disabled/></Field>
                  <Button data-testid="profile-save" onClick={saveProfile}>Update profile</Button>
                  <Button variant="outline" data-testid="logout-everywhere"
                          onClick={async () => {
                            if (window.confirm("Sign out of Mahano on every device, including this one?")) {
                              await logoutEverywhere();
                            }
                          }}>
                    Sign out everywhere
                  </Button>
                </>}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="activity">
            <ActivityPanel isAdmin={false} />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  if (!f) return <div className="text-sm text-muted-foreground">Loading settings…</div>;
  const set = (patch) => setF({ ...f, ...patch });

  const save = async () => {
    try { await update(f); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
  };

  const toggleModule = (m) => {
    const list = f.enabled_modules || [];
    set({ enabled_modules: list.includes(m) ? list.filter((x) => x !== m) : [...list, m] });
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">White-label, branding, modules, terminology, finance, integrations.</p>
        </div>
        <Button data-testid="save-settings" className="ml-auto" onClick={save}><Save className="w-4 h-4 mr-2"/> Save all</Button>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="tab-profile" value="profile">Profile</TabsTrigger>
          <TabsTrigger data-testid="tab-business" value="business">Business</TabsTrigger>
          <TabsTrigger data-testid="tab-workspace" value="workspace">Workspace</TabsTrigger>
          <TabsTrigger data-testid="tab-team" value="team">Team</TabsTrigger>
          <TabsTrigger data-testid="tab-activity" value="activity">Activity</TabsTrigger>
          <TabsTrigger data-testid="tab-finance-settings" value="finance">Finance</TabsTrigger>
          <TabsTrigger data-testid="tab-email" value="email">Email</TabsTrigger>
          <TabsTrigger data-testid="tab-news-feeds" value="news">News</TabsTrigger>
          <TabsTrigger data-testid="tab-google" value="google">Google</TabsTrigger>
          <TabsTrigger data-testid="tab-ai" value="ai">AI</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="space-y-6">
            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Your profile</CardTitle></CardHeader>
              <CardContent className="space-y-3 max-w-md">
                {me && <>
                  <Field label="Name"><Input data-testid="profile-name" value={me.name} onChange={(e) => setMe({...me, name: e.target.value})}/></Field>
                  <Field label="Email"><Input value={me.email} disabled/></Field>
                  <Button data-testid="profile-save" onClick={saveProfile}>Update profile</Button>
                </>}
              </CardContent>
            </Card>

            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Sessions</CardTitle></CardHeader>
              <CardContent className="space-y-4 max-w-md">
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Ticking “Keep me signed in” keeps you logged in on that device for 60 days.
                  Lost a laptop or phone? Sign out everywhere and every device has to log in again.
                </p>
                <Button variant="outline" data-testid="logout-everywhere"
                        onClick={async () => {
                          if (window.confirm("Sign out of Mahano on every device, including this one?")) {
                            await logoutEverywhere();
                          }
                        }}>
                  Sign out everywhere
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="business">
          <div className="space-y-6">
            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Business details</CardTitle></CardHeader>
              <CardContent className="space-y-3 max-w-2xl">
                <Field label="Business name"><Input data-testid="setting-business-name" value={f.business_name || ""} onChange={(e) => set({ business_name: e.target.value })}/></Field>
                <Field label="Product name (shown in sidebar)"><Input data-testid="setting-product-name" value={f.product_name || ""} onChange={(e) => set({ product_name: e.target.value })}/></Field>
                <Field label="Tagline"><Input value={f.tagline || ""} onChange={(e) => set({ tagline: e.target.value })}/></Field>
              </CardContent>
            </Card>

            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Branding</CardTitle></CardHeader>
              <CardContent className="space-y-5 max-w-2xl">
                <LogoField
                  logoUrl={f.logo_url}
                  onChange={(v) => set({ logo_url: v })}
                />
                <div className="space-y-2">
                  <Label className="text-xs">Theme preset</Label>
                  <div className="flex flex-wrap gap-2" data-testid="theme-presets">
                    {THEME_PRESETS.map((preset) => {
                      const active = (f.primary_color || "").toLowerCase() === preset.accent.toLowerCase();
                      return (
                        <button key={preset.id} type="button"
                                data-testid={`theme-preset-${preset.id}`}
                                onClick={() => set({ primary_color: preset.accent, secondary_color: preset.surface })}
                                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                                  active ? "border-[hsl(var(--studio-primary))] bg-accent"
                                         : "border-border hover:bg-accent"}`}>
                          <span className="h-3.5 w-3.5 rounded-full border border-black/10"
                                style={{ background: preset.accent }} />
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ColorField
                    label="Accent colour"
                    testid="setting-primary-color"
                    value={f.primary_color || "#30B5A1"}
                    onChange={(v) => set({ primary_color: v })}
                  />
                  <ColorField
                    label="Secondary surface"
                    testid="setting-secondary-color"
                    value={f.secondary_color || "#E7DDD0"}
                    onChange={(v) => set({ secondary_color: v })}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Default theme for this workspace</Label>
                    <Select value={f.default_theme || "system"}
                            onValueChange={(v) => set({ default_theme: v })}>
                      <SelectTrigger data-testid="setting-default-theme"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="system">Follow the device</SelectItem>
                        <SelectItem value="light">Light (Soft Ivory)</SelectItem>
                        <SelectItem value="dark">Dark (Midnight Ink)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                      Applies until someone picks their own theme in the top bar.
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Accent colours apply live across the app — buttons, links, active nav, charts and focus rings.
                </p>
                <div className="pt-2 border-t border-border flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">See it as a client does</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Check an invoice, a reminder, a campaign and the sign-up form before anyone else does.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" data-testid="open-client-preview"
                          onClick={() => setPreviewOpen(true)}>
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview as client
                  </Button>
                </div>
                <div className="pt-2 border-t border-border space-y-4">
                  <div>
                    <div className="text-sm font-medium">Brand assets</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Drop your final logo files in here and they replace the built-in mark everywhere —
                      app, emails, invoices and public forms.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ImageUploadField label="Lockup — light backgrounds" testId="brand-logo-light"
                                      value={f.brand_logo_light_url || ""} hint="Full logo + wordmark, PNG or SVG."
                                      onChange={(v) => set({ brand_logo_light_url: v })} />
                    <ImageUploadField label="Lockup — dark backgrounds" testId="brand-logo-dark"
                                      value={f.brand_logo_dark_url || ""} hint="Light version of the lockup."
                                      onChange={(v) => set({ brand_logo_dark_url: v })} />
                    <ImageUploadField label="Icon only" testId="brand-icon" round
                                      value={f.brand_icon_url || ""} hint="Square mark, used for app icons."
                                      onChange={(v) => set({ brand_icon_url: v })} />
                    <ImageUploadField label="Favicon" testId="brand-favicon"
                                      value={f.brand_favicon_url || ""} hint="32×32 or 48×48 PNG / SVG."
                                      onChange={(v) => set({ brand_favicon_url: v })} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Typography</CardTitle></CardHeader>
              <CardContent className="max-w-lg">
                <FontPairingField
                  value={getFontPairing(f.font_pairing).id}
                  onChange={(v) => { set({ font_pairing: v }); applyFontPairing(v); }}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="workspace">
          <div className="space-y-6">
            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Modules & labels</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground max-w-2xl">
                  Toggle which modules appear in your sidebar and rename them to match your workflow — <i>People</i> can become <i>Contacts</i>, <i>Finance</i> can be <i>Money</i>. Uncheck a module to hide it entirely.
                </p>
                <div className="space-y-2 max-w-3xl" data-testid="workspace-module-list">
                  <div className="grid grid-cols-[24px_1fr_minmax(180px,1fr)] gap-3 items-center px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <div />
                    <div>Module</div>
                    <div>Rename to (optional)</div>
                  </div>
                  {MODULES.map((m) => {
                    const enabled = f.enabled_modules?.includes(m);
                    return (
                      <div key={m} className="grid grid-cols-[24px_1fr_minmax(180px,1fr)] gap-3 items-center px-2 py-1.5 rounded-md hover:bg-accent/40" data-testid={`module-row-${m}`}>
                        <input
                          type="checkbox"
                          checked={!!enabled}
                          onChange={() => toggleModule(m)}
                          className="w-4 h-4 accent-[hsl(var(--studio-primary))]"
                          data-testid={`module-toggle-${m}`}
                        />
                        <div className="text-sm capitalize">{m}</div>
                        <Input
                          value={f.module_labels?.[m] || ""}
                          placeholder={m}
                          disabled={!enabled}
                          onChange={(e) => set({ module_labels: { ...(f.module_labels || {}), [m]: e.target.value } })}
                          className="h-8 text-sm"
                          data-testid={`module-label-${m}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Product tour & getting started</CardTitle></CardHeader>
              <CardContent className="space-y-3 max-w-2xl">
                <p className="text-xs text-muted-foreground">
                  A guided walkthrough of every part of Mahano — the morning brief, day planning, projects, tasks, invoicing and white-labelling. Runs on the real app, about 90 seconds.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={startTour} data-testid="settings-start-tour">
                    <Compass className="w-4 h-4 mr-2"/> Start the walkthrough
                  </Button>
                  <Button variant="outline" data-testid="settings-show-checklist" onClick={async () => {
                    await update({ checklist_dismissed: false });
                    toast.success("Checklist is back on Home");
                  }}>
                    <ListChecks className="w-4 h-4 mr-2"/> Show the 5-step checklist
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="studio-card">
              <CardHeader><CardTitle className="text-base">Working hours & scheduling</CardTitle></CardHeader>
              <CardContent className="space-y-5 max-w-3xl">
                <p className="text-xs text-muted-foreground">
                  Reschedule Day packs each task into its own <b>schedule window</b> — so after-hours work never lands mid-morning and weekend jobs only land on weekends. Add as many windows as your life needs.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="Timezone">
                    <Input value={f.timezone || ""} placeholder="Australia/Sydney" data-testid="settings-timezone"
                      onChange={(e) => set({ timezone: e.target.value })}/>
                  </Field>
                  <Field label="Buffer between tasks (min)">
                    <Input type="number" min={0} step={5} value={f.slot_buffer_minutes ?? 10} data-testid="settings-slot-buffer"
                      onChange={(e) => set({ slot_buffer_minutes: parseInt(e.target.value) || 0 })}/>
                  </Field>
                </div>

                <div className="space-y-3" data-testid="schedule-windows-editor">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Schedule windows</Label>
                    <Button variant="outline" size="sm" data-testid="add-schedule-window" onClick={() => {
                      const list = f.schedule_windows || [];
                      set({ schedule_windows: [...list, {
                        key: `window_${Date.now().toString(36)}`,
                        name: "New window", days: [6, 7], start: "09:00", end: "12:00",
                      }] });
                    }}><Plus className="w-3.5 h-3.5 mr-1.5"/> Add window</Button>
                  </div>
                  {(f.schedule_windows || []).map((w, idx) => {
                    const patchWindow = (patch) => {
                      const list = [...(f.schedule_windows || [])];
                      list[idx] = { ...list[idx], ...patch };
                      set({ schedule_windows: list });
                    };
                    return (
                      <div key={w.key} className="rounded-xl border border-border p-3 space-y-3" data-testid={`schedule-window-${w.key}`}>
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="flex-1 min-w-[160px] space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground">Name</Label>
                            <Input value={w.name || ""} data-testid={`window-name-${w.key}`} className="h-9"
                              onChange={(e) => patchWindow({ name: e.target.value })}/>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground">From</Label>
                            <Input type="time" value={w.start || "09:00"} data-testid={`window-start-${w.key}`} className="h-9 w-28"
                              onChange={(e) => patchWindow({ start: e.target.value })}/>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground">To</Label>
                            <Input type="time" value={w.end || "18:00"} data-testid={`window-end-${w.key}`} className="h-9 w-28"
                              onChange={(e) => patchWindow({ end: e.target.value })}/>
                          </div>
                          <Button variant="ghost" size="sm" data-testid={`delete-window-${w.key}`} className="text-[hsl(var(--studio-danger))]"
                            onClick={() => set({ schedule_windows: (f.schedule_windows || []).filter((_, i) => i !== idx) })}>
                            <Trash2 className="w-3.5 h-3.5"/>
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {[["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6], ["Sun", 7]].map(([label, n]) => {
                            const on = (w.days || []).includes(n);
                            return (
                              <button
                                key={n}
                                data-testid={`window-${w.key}-day-${n}`}
                                onClick={() => patchWindow({ days: on ? (w.days || []).filter((x) => x !== n) : [...(w.days || []), n].sort() })}
                                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${on
                                  ? "bg-[hsl(var(--studio-primary))]/15 text-[hsl(var(--studio-primary))] border-[hsl(var(--studio-primary)/0.35)] font-medium"
                                  : "text-muted-foreground border-border hover:bg-accent"}`}
                              >{label}</button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="studio-card border-[hsl(var(--studio-danger)/0.3)]">
              <CardHeader><CardTitle className="text-base">Reset workspace</CardTitle></CardHeader>
              <CardContent className="space-y-3 max-w-2xl">
                <p className="text-xs text-muted-foreground">
                  Wipes every project, task, contact, company, invoice, document, file and calendar entry in this workspace.
                  Your account, branding and settings stay exactly as they are. There is no undo.
                </p>
                {resetSummary && (
                  <div className="rounded-lg border border-border p-3 text-xs grid grid-cols-2 md:grid-cols-4 gap-y-1 gap-x-4" data-testid="reset-summary">
                    {Object.entries(resetSummary.counts).filter(([, n]) => n > 0).map(([k, n]) => (
                      <div key={k} className="flex justify-between gap-2">
                        <span className="text-muted-foreground capitalize">{k.replace("_", " ")}</span>
                        <span className="font-medium tabular-nums">{n}</span>
                      </div>
                    ))}
                    <div className="col-span-2 md:col-span-4 pt-1 border-t border-border flex justify-between">
                      <span className="text-muted-foreground">Total records</span>
                      <span className="font-semibold tabular-nums">{resetSummary.total}</span>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    placeholder="Type RESET to confirm"
                    className="max-w-[220px]"
                    data-testid="reset-confirm-input"
                  />
                  <Button
                    variant="destructive"
                    disabled={resetConfirm !== "RESET" || resetting}
                    data-testid="reset-workspace-btn"
                    onClick={async () => {
                      setResetting(true);
                      try {
                        const { data } = await api.post("/workspace/reset", { confirm: "RESET" });
                        toast.success(`Workspace cleared — ${data.total} records removed`);
                        setResetConfirm("");
                        await refresh?.();
                        navigate("/");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Reset failed");
                      } finally { setResetting(false); }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2"/> {resetting ? "Clearing…" : "Reset workspace"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="team">
          <TeamPanel ownerId={me?.workspace_id || me?.id} />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityPanel isAdmin />
        </TabsContent>

        <TabsContent value="finance">
          <Card className="studio-card">
            <CardHeader><CardTitle className="text-base">Finance defaults</CardTitle></CardHeader>
            <CardContent className="space-y-3 max-w-md">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Currency"><Input value={f.currency || ""} onChange={(e) => set({ currency: e.target.value })}/></Field>
                <Field label="Invoice prefix"><Input value={f.invoice_prefix || ""} onChange={(e) => set({ invoice_prefix: e.target.value })}/></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tax label"><Input value={f.tax_label || ""} onChange={(e) => set({ tax_label: e.target.value })}/></Field>
                <Field label="Tax rate (%)"><Input type="number" step="0.1" value={f.tax_rate ?? 0} onChange={(e) => set({ tax_rate: parseFloat(e.target.value) || 0 })}/></Field>
              </div>
            </CardContent>
          </Card>

          <Card className="studio-card mt-4">
            <CardHeader><CardTitle className="text-base">Overdue invoice reminders</CardTitle></CardHeader>
            <CardContent className="space-y-4 max-w-2xl">
              <div className="flex items-center gap-2">
                <Switch checked={!!f.reminders_enabled} data-testid="reminders-enabled"
                  onCheckedChange={(v) => set({ reminders_enabled: v })}/>
                <span className="text-sm">Chase overdue invoices automatically</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Runs daily at 08:00 UTC and emails through your provider in Settings → Email. Reminders are logged on each invoice, and you can pause any single invoice from its edit dialog.
              </p>
              <Field label="Chase on these days past due (comma separated)">
                <Input
                  value={(f.reminder_days || [3, 7, 14]).join(", ")}
                  data-testid="reminder-days"
                  onChange={(e) => set({ reminder_days: e.target.value.split(",").map((x) => parseInt(x.trim())).filter((n) => !isNaN(n) && n > 0) })}
                />
              </Field>
              <p className="text-[11px] text-muted-foreground -mt-2">After the last day, Mahano nudges weekly.</p>
              <Field label="Subject"><Input value={f.reminder_subject || ""} data-testid="reminder-subject" onChange={(e) => set({ reminder_subject: e.target.value })}/></Field>
              <Field label="Message">
                <Textarea rows={8} value={f.reminder_body || ""} data-testid="reminder-body" onChange={(e) => set({ reminder_body: e.target.value })}/>
              </Field>
              <p className="text-[11px] text-muted-foreground -mt-2">
                Placeholders: {"{client_name} {number} {amount} {due_date} {days_overdue} {business_name}"}
              </p>
              {reminderPreview && (
                <div className="rounded-lg border border-border p-3 text-xs space-y-1" data-testid="reminder-preview">
                  <div className="font-medium">{reminderPreview.count} invoice(s) would be chased today</div>
                  {!reminderPreview.email_ready && (
                    <div className="text-[hsl(var(--studio-danger))]">
                      {reminderPreview.email_provider_label || "Email"} isn't configured — reminders will be logged, not emailed. Set it up in the Email tab.
                    </div>
                  )}
                  {reminderPreview.items.slice(0, 5).map((i) => (
                    <div key={i.invoice_id} className="text-muted-foreground">
                      {i.number} · {i.client_name || "client"} · {i.days_overdue}d overdue · {i.to || "no email on file"}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" data-testid="reminder-refresh-preview" onClick={loadReminderPreview}>
                  Refresh preview
                </Button>
                <Button data-testid="reminder-send-now" onClick={async () => {
                  try {
                    const { data } = await api.post("/invoices/reminders/run");
                    toast.success(`Chased ${data.sent} of ${data.processed} invoice(s)`);
                    loadReminderPreview();
                  } catch { toast.error("Could not send reminders"); }
                }}>
                  <Send className="w-4 h-4 mr-2"/> Send reminders now
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <EmailPanel settings={f} onSet={set} onSaveAll={save} />
        </TabsContent>

        <TabsContent value="news">
          <Card className="studio-card">
            <CardHeader><CardTitle className="text-base">News feeds & topics</CardTitle></CardHeader>
            <CardContent className="space-y-6 max-w-2xl">
              <div className="space-y-2">
                <Label className="text-xs">Topics you care about  <span className="text-[hsl(var(--halo-teal))] ml-1 font-medium">Primary</span></Label>
                <p className="text-xs text-muted-foreground">
                  These drive an <b>AI web-search</b>: on each daily open (and any time you press Refresh) Claude
                  scans the live web for fresh headlines matching your topics — new releases, funding calls,
                  industry moves, festival lineups, product launches. Comma-separated.
                </p>
                <Input
                  data-testid="news-topics-input"
                  value={(f.news_topics || []).join(", ")}
                  onChange={(e) => set({ news_topics: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="Film funding, Screen Australia grants, streaming deals, new theatrical releases…"
                />
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(f.news_topics || []).map((t) => (
                    <span key={t} className="text-xs px-2 py-1 rounded-md bg-[hsl(var(--studio-primary)/0.10)] text-[hsl(var(--studio-primary))] border border-[hsl(var(--studio-primary)/0.20)]">{t}</span>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <RssFeedsField
                  value={f.news_feeds || []}
                  onChange={(list) => set({ news_feeds: list })}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="google">
          <GooglePanel settings={f} onSet={set} onSaveAll={save} />
        </TabsContent>

        <TabsContent value="ai">
          <Card className="studio-card">
            <CardHeader><CardTitle className="text-base">AI settings</CardTitle></CardHeader>
            <CardContent className="space-y-3 max-w-md">
              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div>
                  <div className="text-sm font-medium">Enable AI</div>
                  <div className="text-xs text-muted-foreground">Powers Morning Brief, suggestions and news summaries.</div>
                </div>
                <Switch checked={!!f.ai_enabled} onCheckedChange={(v) => set({ ai_enabled: v })}/>
              </div>
              <Field label="Provider">
                <Select value={f.ai_provider} onValueChange={(v) => set({ ai_provider: v })}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anthropic">Anthropic</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="gemini">Gemini</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Model">
                <Select value={f.ai_model} onValueChange={(v) => set({ ai_model: v })}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</SelectItem>
                    <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.6</SelectItem>
                    <SelectItem value="gpt-5.2">GPT-5.2</SelectItem>
                    <SelectItem value="gpt-5.4">GPT-5.4</SelectItem>
                    <SelectItem value="gemini-3-flash-preview">Gemini 3 Flash</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ClientPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

/* -------------------- Branding: Logo upload + URL -------------------- */
function LogoField({ logoUrl, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 750 * 1024) {
      toast.error("Logo must be under 750 KB. Try a smaller PNG or SVG.");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      onChange(dataUrl);
      toast.success("Logo attached — remember to Save all.");
    } catch {
      toast.error("Could not read that file");
    } finally {
      setUploading(false);
    }
  };

  const hasLogo = !!(logoUrl && logoUrl.length);
  const isDataUrl = hasLogo && logoUrl.startsWith("data:");
  return (
    <div className="space-y-2" data-testid="logo-field">
      <Label className="text-xs">Logo</Label>
      <div className="flex items-start gap-4">
        <div
          className="w-20 h-20 rounded-xl border border-dashed border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0"
          data-testid="logo-preview"
        >
          {hasLogo ? (
            <img src={logoUrl} alt="Logo preview" className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">No logo</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              data-testid="logo-file-input"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="logo-upload-btn"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {uploading ? "Reading…" : hasLogo ? "Replace logo" : "Upload logo"}
            </Button>
            {hasLogo && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-testid="logo-clear-btn"
                onClick={() => onChange("")}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Or paste a hosted URL below. PNG, SVG, JPG or WebP up to 750 KB.
          </p>
          <Input
            data-testid="logo-url-input"
            placeholder="https://…/logo.png"
            value={isDataUrl ? "" : (logoUrl || "")}
            onChange={(e) => onChange(e.target.value)}
          />
          {isDataUrl && (
            <p className="text-[11px] text-muted-foreground">Currently using an uploaded file. Paste a URL to switch.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Branding: Colour picker + hex -------------------- */
function ColorField({ label, value, onChange, testid }) {
  const normalized = /^#([0-9a-fA-F]{6})$/.test(value) ? value : "#30B5A1";
  return (
    <div className="space-y-1.5" data-testid={testid || "color-field"}>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <label
          className="relative w-10 h-10 rounded-lg border border-border shadow-inner cursor-pointer overflow-hidden shrink-0"
          style={{ background: normalized }}
          data-testid={`${testid || "color"}-swatch`}
        >
          <input
            type="color"
            value={normalized}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid={`${testid || "color"}-picker`}
            aria-label={`${label} picker`}
          />
        </label>
        <Input
          value={value || ""}
          onChange={(e) => {
            let v = e.target.value.trim();
            if (v && !v.startsWith("#")) v = "#" + v;
            onChange(v.toUpperCase());
          }}
          placeholder="#30B5A1"
          className="font-mono text-sm"
          maxLength={7}
          data-testid={`${testid || "color"}-hex`}
        />
      </div>
    </div>
  );
}

/* -------------------- Appearance: Font pairing dropdown -------------------- */
function FontPairingField({ value, onChange }) {
  const active = getFontPairing(value);
  return (
    <div className="space-y-1.5" data-testid="font-pairing-field">
      <Label className="text-xs">Font pairing</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="font-pairing-select"><SelectValue placeholder="Choose a pairing" /></SelectTrigger>
        <SelectContent>
          {FONT_PAIRINGS.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`font-option-${p.id}`}>
              <span style={{ fontFamily: p.stack }}>{p.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div
        className="mt-2 rounded-lg border border-border p-4"
        style={{ fontFamily: active.stack }}
        data-testid="font-pairing-preview"
      >
        <div className="text-xl font-semibold tracking-tight">The quick brown fox jumps.</div>
        <div className="text-sm text-muted-foreground mt-1">1234567890 · Mahano · {active.label}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{active.description}</div>
      </div>
    </div>
  );
}

/* -------------------- News: RSS textarea that accepts Enter -------------------- */
function RssFeedsField({ value, onChange }) {
  // Mirror value in local text state so Enter keys become real newlines and
  // trailing empty lines aren't clobbered mid-typing.
  const [text, setText] = useState((value || []).join("\n"));
  const initialised = useRef(false);

  // Sync when the parent value changes for reasons other than typing here
  // (e.g. fresh settings load) — but only when it materially differs.
  const joined = useMemo(() => (value || []).join("\n"), [value]);
  useEffect(() => {
    if (!initialised.current) { initialised.current = true; return; }
    const currentParsed = text.split("\n").map((s) => s.trim()).filter(Boolean).join("\n");
    if (currentParsed !== joined) setText(joined);
  }, [joined, text]);

  const commit = (nextText) => {
    const list = nextText.split("\n").map((s) => s.trim()).filter(Boolean);
    onChange(list);
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">RSS feed URLs  <span className="text-muted-foreground ml-1">Optional</span></Label>
      <p className="text-xs text-muted-foreground">
        {"Niche or specialist feeds the AI won't naturally discover (e.g. a specific grant board or trade newsletter). "}
        One URL per line, press Enter for a new line.
      </p>
      <Textarea
        rows={5}
        data-testid="news-feeds-textarea"
        value={text}
        placeholder={"https://screenaustralia.gov.au/news.rss\nhttps://variety.com/feed"}
        onChange={(e) => { setText(e.target.value); commit(e.target.value); }}
      />
      <p className="text-[11px] text-muted-foreground">{(value || []).length} feed{(value || []).length === 1 ? "" : "s"} configured.</p>
    </div>
  );
}

function GooglePanel({ settings, onSet, onSaveAll }) {
  const [status, setStatus] = useState({ connected: false, email: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/google/status").then(({ data }) => setStatus(data));
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      // Persist current form (client id + secret) first so backend can use them
      await onSaveAll();
      const { data } = await api.get("/google/connect", { params: { redirect_origin: window.location.origin } });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google connect");
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    await api.post("/google/disconnect");
    setStatus({ connected: false, email: null });
    toast.success("Google disconnected");
  };

  return (
    <Card className="studio-card">
      <CardHeader><CardTitle className="text-base">Google Calendar</CardTitle></CardHeader>
      <CardContent className="space-y-4 max-w-2xl">
        <div className="rounded-lg border border-border p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Status</div>
            {status.connected ? (
              <div className="text-xs text-muted-foreground mt-0.5">Connected as <b>{status.email}</b></div>
            ) : (
              <div className="text-xs text-muted-foreground mt-0.5">Not connected. Paste your Google Client ID and Secret, save, then connect.</div>
            )}
          </div>
          {status.connected ? (
            <Button data-testid="google-disconnect" variant="outline" onClick={disconnect}>Disconnect</Button>
          ) : (
            <Button data-testid="google-connect" onClick={connect} disabled={busy}>{busy ? "Redirecting…" : "Connect Google"}</Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">
          To enable calendar sync you need a Google OAuth Client. Create one in the
          <a className="text-[hsl(var(--studio-primary))] underline mx-1" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer">Google Cloud Console</a>,
          enable the Google Calendar API, and add
          <code className="mx-1 px-1.5 py-0.5 rounded bg-accent text-[11px]">{window.location.origin}/auth/google/callback</code>
          as an Authorized Redirect URI. Then paste the credentials below and press Save all.
        </p>

        <Field label="Google Client ID"><Input data-testid="google-client-id" value={settings.google_client_id || ""} onChange={(e) => onSet({ google_client_id: e.target.value })} placeholder="1234567890-abc.apps.googleusercontent.com"/></Field>
        <Field label="Google Client Secret"><Input data-testid="google-client-secret" type="password" value={settings.google_client_secret || ""} onChange={(e) => onSet({ google_client_secret: e.target.value })}/></Field>
        <Field label="Calendar ID (leave 'primary' for your main calendar)"><Input value={settings.google_calendar_id || "primary"} onChange={(e) => onSet({ google_calendar_id: e.target.value })}/></Field>
      </CardContent>
    </Card>
  );
}
